// Locale-aware accessors for Viator tour copy, with a safe fallback to the raw
// feed text so un-translated or partially-failed rows still render. The tours
// analog of the app's event-content.ts. Every consumer that shows a tour's title /
// description / meta summary should read through these rather than touching
// `tour.title` / `tour.description` directly.
//
// The localized copy is produced by the translate-tours Edge Function; see
// docs/tour-translation.md.

import type { Locale } from './locales';
import type { Tour, TourFactList } from '../tours';

/** Localized title, falling back to the original (English) Viator title. */
export function localizedTourTitle(tour: Tour, locale: Locale): string {
  return tour.translations?.[locale]?.title ?? tour.title;
}

/**
 * Localized search-result title for the page `<title>` — short, keyword-first,
 * generated separately because Viator product names run to 128 chars and a
 * truncated one loses the part that tells tours apart. The full title stays in
 * the <h1>. Falls back to the localized title.
 */
export function localizedTourSeoTitle(tour: Tour, locale: Locale): string {
  return tour.translations?.[locale]?.seoTitle || localizedTourTitle(tour, locale);
}

/** Localized body description, falling back to the raw Viator description. */
export function localizedTourDescription(tour: Tour, locale: Locale): string | null {
  return tour.translations?.[locale]?.description ?? tour.description ?? null;
}

/**
 * Localized ≤160-char SEO meta summary. No feed fallback — the source has no
 * equivalent field, so callers should fall back to a truncated description.
 */
export function localizedTourSummary(tour: Tour, locale: Locale): string | null {
  return tour.translations?.[locale]?.summary ?? null;
}

/**
 * A tours.details list (included / excluded / knowBefore) in the page's
 * language, falling back to the supplier's English lines. `lang` is 'en' when
 * the lines shown are English on a non-English page (for the list's lang
 * attribute), else undefined.
 */
export function localizedTourFacts(
  tour: Tour,
  list: TourFactList,
  locale: Locale,
): { items: string[]; lang: 'en' | undefined } {
  const translated = tour.translations?.[locale]?.facts?.[list];
  if (translated?.length) return { items: translated, lang: undefined };
  return { items: tour.details?.[list] ?? [], lang: locale === 'en' ? undefined : 'en' };
}
